import { Button, Group, Modal, Text, ButtonGroup } from '@landr/maestro';
import { Trans } from '@lingui/macro';
import React, { useCallback } from 'react';
import { useNavigate, useMatch } from 'react-router-dom';
import { areEqual, evictCacheByKeys } from 'helpers';
import { useMutation } from '@apollo/client';
import { DeleteUserCollectionMutation } from 'apollo/userCollection/userCollection.gql';
import { QueryConfig } from 'apollo/userCollection/queryConfig';
import { useCollectionModalContext } from 'modals/CollectionModal/CollectionModalContext';
import { ModalProps } from 'modals/Modal.types';
import { Collection } from 'types';
import { RoutePaths } from 'types/enums';
import { useToLocalizedPath } from 'hooks/useToLocalizedPath/useToLocalizedPath';

interface DeleteCollectionModalContentProps extends ModalProps {
    collection: Collection | null;
    isLoading: boolean;
    handleDeleteCollection: () => void;
}

export const DeleteCollectionModalContent: React.FC<
    DeleteCollectionModalContentProps
> = ({ handleClose, handleDeleteCollection, isLoading, collection }) => {
    if (!collection?.guid) {
        return null;
    }

    return (
        <>
            <Modal.Content>
                <Modal.Section>
                    <Group direction="vertical" gutter="xl" mt="sm">
                        <Group.Item>
                            <Text size="xl" fontWeight="bold">
                                <Trans>Delete collection?</Trans>
                            </Text>
                        </Group.Item>
                        <Group.Item>
                            <Text
                                size="md"
                                contrast={'low'}
                                style={{ overflowWrap: 'break-word' }}
                            >
                                <Trans>
                                    {collection.name} will be permanently
                                    deleted from your collections.
                                </Trans>
                            </Text>
                        </Group.Item>
                    </Group>
                </Modal.Section>
            </Modal.Content>
            <Modal.Footer>
                <ButtonGroup>
                    <Button
                        variant="secondary"
                        kind="minimal"
                        onClick={handleClose as () => void}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="danger"
                        onClick={handleDeleteCollection}
                        isLoading={isLoading}
                    >
                        Delete
                    </Button>
                </ButtonGroup>
            </Modal.Footer>
        </>
    );
};

export const DeleteCollectionModal: React.FC<ModalProps> = ({
    isOpen,
    handleClose,
}) => {
    const { collection, setCollection } = useCollectionModalContext();
    const navigate = useNavigate();
    const toLocalizedPath = useToLocalizedPath();

    const isUserCollectionDetailsPage = !!useMatch(
        toLocalizedPath(`/${RoutePaths.Collections}/:slug`),
    );

    const [requestDelete, { loading }] = useMutation(
        DeleteUserCollectionMutation,
        {
            variables: { collectionGuid: collection?.guid },
            update: (cache) => {
                evictCacheByKeys(cache, [
                    collection?.guid,
                    QueryConfig.pathToItems,
                    'userCollectionsFeed',
                ]);
            },
            onCompleted: () => {
                handleClose();
                setCollection(null);
                if (isUserCollectionDetailsPage) {
                    navigate(toLocalizedPath(`/${RoutePaths.Collections}`));
                }
            },
        },
    );

    const deleteCollection = useCallback(() => {
        requestDelete();
    }, [requestDelete]);

    return (
        <Modal isOpen={isOpen} onClose={handleClose}>
            <DeleteCollectionModalContent
                isOpen={isOpen}
                handleClose={handleClose}
                handleDeleteCollection={deleteCollection}
                isLoading={loading}
                collection={collection}
            />
        </Modal>
    );
};

export default React.memo(DeleteCollectionModal, (prevProps, nextProps) => {
    if (areEqual(prevProps, nextProps)) {
        return true;
    }

    return false;
});
